import { hasQualitySection } from '../../cabinetAccess';
import TeamDrawingWorkspace from './TeamDrawingWorkspace';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Badge, Box, Button, Chip, CircularProgress, Input, Option, Select, Sheet,
  Stack, Tab, TabList, Tabs, Typography,
} from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";

import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';

import { LayoutContext } from "../../context";
import {
  APIQualityAcceptRework, APIQualityApprove, APIQualityEvents,
  APIQualityFilterOptions, APIQualityReviews, APIQualityRework,
} from "../../api";

import QualityEventCard from "../../components/Quality/QualityEventCard";
import QualityReworkModal from "../../components/Quality/QualityReworkModal";
import QualityEventModal from "../../components/Quality/QualityEventModal";
import { SCOPES } from "../../components/Quality/qualityTheme";

const EMPTY_TEXT = {
  pending: 'Непроверенных чертежей нет — всё разобрано.',
  rework: 'Ничего не возвращено на доработку.',
  in_progress: 'Никто сейчас не дорабатывает.',
  done: 'Принятых работ пока нет.',
  all: 'Через контроль ещё ничего не проходило.',
};

export default function QualityPage() {
  // ChatModal висит глобально в App.js и управляется контекстом — своего
  // экземпляра тут не заводим, иначе на экране окажутся два чата.
  const { tokenEmployee, account, notify, setChatModal } = useContext(LayoutContext);

  const [scope, setScope] = useState('pending');
  const [rows, setRows] = useState([]);
  const [counters, setCounters] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [options, setOptions] = useState({ draftsmen: [], cities: [] });

  const [search, setSearch] = useState('');
  const [draftsman, setDraftsman] = useState('');
  const [city, setCity] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [reworkFor, setReworkFor] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [reviewsById, setReviewsById] = useState({});
  const loadEpoch = useRef(0);
  const actionsInFlight = useRef(new Set());
  useEffect(() => () => { loadEpoch.current += 1; }, []);

  const isQualityMaster = hasQualitySection(account);

  const load = useCallback(async () => {
    if (!tokenEmployee || !isQualityMaster) return;
    const epoch = ++loadEpoch.current;
    setLoading(true); setLoadError('');
    const params = { scope, limit: 100 };
    if (search.trim()) params.search = search.trim();
    if (draftsman) params.draftsman = draftsman;
    if (city) params.city = city;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    try {
      const res = await APIQualityEvents(params, tokenEmployee);
      if (epoch !== loadEpoch.current) return null;
      if (res?.status !== 'ok') {
        const message = typeof (res?.message || res?.detail) === 'string' ? res.message || res.detail : 'Не удалось загрузить список';
        setLoadError(message);
        notify?.(message, 'danger');
        return null;
      }
      setRows(res.events || []);
      setCounters(res.counters || {});
      return res;
    } catch (failure) {
      if (epoch === loadEpoch.current) {
        const message = 'Не удалось обновить список. Повторите запрос.';
        setLoadError(message); notify?.(message, 'danger');
      }
      return null;
    } finally {
      if (epoch === loadEpoch.current) setLoading(false);
    }
  }, [tokenEmployee, isQualityMaster, scope, search, draftsman, city, dateFrom, dateTo, notify]);
  const latestLoad = useRef(load);
  latestLoad.current = load;

  useEffect(() => {
    if (!tokenEmployee) return undefined;
    // Поиск набирают по букве — без паузы каждый символ уходил бы запросом.
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search, tokenEmployee]);

  useEffect(() => {
    if (!tokenEmployee || !isQualityMaster) return;
    APIQualityFilterOptions(tokenEmployee).then((d) => {
      if (d?.status === 'ok') setOptions({ draftsmen: d.draftsmen || [], cities: d.cities || [] });
    });
  }, [tokenEmployee]);

  const loadReviews = async (eventId) => {
    if (reviewsById[eventId] !== undefined) return;
    setReviewsById((p) => ({ ...p, [eventId]: undefined }));
    const d = await APIQualityReviews(eventId, tokenEmployee);
    setReviewsById((p) => ({ ...p, [eventId]: d?.reviews || [] }));
  };

  const refreshAfterAction = async (eventId) => {
    setReviewsById(previous => {
      const next = { ...previous };
      delete next[eventId];
      return next;
    });
    return latestLoad.current();
  };

  const actionResult = (result, fallback) => {
    if (result?.status === 'ok') return result;
    const message = result?.message || result?.detail;
    return { ...result, status: 'error', message: typeof message === 'string' ? message : fallback };
  };

  const approve = async (card, stars) => {
    if (!card?.id || actionsInFlight.current.has(card.id)) {
      return { status: 'error', message: 'Решение по этому событию уже отправляется.' };
    }
    actionsInFlight.current.add(card.id);
    let result;
    try {
      result = actionResult(await APIQualityApprove(card.id, { stars, qa_round: card.qa_round }, tokenEmployee), 'Не удалось принять работу');
      if (result.status !== 'ok') notify?.(result.message, 'danger');
      else if (result.warning) notify?.(result.warning, 'warning');
      else notify?.('Проверка пройдена, работа готова к выдаче', 'success');
    } catch (failure) {
      result = { status: 'error', message: 'Не удалось отправить решение. Проверьте связь и статус события.' };
      notify?.(result.message, 'danger');
    } finally {
      actionsInFlight.current.delete(card.id);
      await refreshAfterAction(card.id);
    }
    return result;
  };

  const submitRework = async ({ comment, stars, files }) => {
    const card = reworkFor;
    if (!card?.id || actionsInFlight.current.has(card.id)) {
      return { status: 'error', message: 'Решение по этому событию уже отправляется.' };
    }
    actionsInFlight.current.add(card.id);
    let result;
    try {
      result = actionResult(await APIQualityRework(card.id, { comment, stars, files, qa_round: card.qa_round }, tokenEmployee), 'Не удалось отправить на доработку');
      if (result.status === 'ok') notify?.('Отправлено на доработку, чертёжник уведомлён', 'success');
    } catch (failure) {
      result = { status: 'error', message: 'Не удалось отправить замечания. Проверьте связь и статус события.' };
    } finally {
      actionsInFlight.current.delete(card.id);
      await refreshAfterAction(card.id);
    }
    return result;
  };

  // Открываем СВОЮ компактную карточку, а не общую модалку события.
  //
  // Раньше здесь искали событие в общем списке listEvents — и у контролёра
  // честно не находили: его список грузится отдельно и карточку с проверки
  // содержать не обязан. Теперь данные приходят по нажатию одним запросом,
  // и ждать загрузки чего бы то ни было не нужно.
  const openEvent = (eventId) => setDetailId(eventId);

  const resetFilters = () => {
    setSearch(''); setDraftsman(''); setCity(''); setDateFrom(''); setDateTo('');
  };
  const filtersOn = !!(search || draftsman || city || dateFrom || dateTo);

  if (!isQualityMaster) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography level="title-md">Раздел доступен мастеру по качеству</Typography>
      </Box>
    );
  }

  return (
    <Box component={motion.section}
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 }, width: '100%', minWidth: 0, boxSizing: 'border-box', maxWidth: 1180, mx: 'auto' }}
    >
      <TeamDrawingWorkspace />
      {/* ── Шапка ── */}
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Sheet variant="soft" sx={{
          width: 42, height: 42, borderRadius: '12px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          background: 'linear-gradient(135deg,#ede9fe,#ddd6fe)',
        }}>
          <FactCheckRoundedIcon sx={{ color: '#5b21b6' }} />
        </Sheet>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-lg" sx={{ fontWeight: 800 }}>Контроль качества</Typography>
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            Проверьте текущий комплект — после принятия работа будет готова к выдаче
          </Typography>
        </Box>
        <Button size="sm" variant="soft" color="neutral"
          startDecorator={<RefreshRoundedIcon />} loading={loading}
          onClick={load}>
          Обновить
        </Button>
      </Stack>

      {/* ── Вкладки ── */}
      <Tabs value={scope} onChange={(_, v) => v && setScope(v)} sx={{ bgcolor: 'transparent', mb: 1.5 }}>
        <TabList
          disableUnderline
          sx={{
            gap: 0.5, p: 0.5, borderRadius: '12px', bgcolor: 'background.level1',
            overflowX: 'auto', flexWrap: 'nowrap',
            '&::-webkit-scrollbar': { display: 'none' },
            [`& .MuiTab-root`]: { borderRadius: '9px', flex: 'none', whiteSpace: 'nowrap' },
            [`& .Mui-selected`]: { bgcolor: 'background.surface', boxShadow: 'sm', fontWeight: 700 },
          }}
        >
          {SCOPES.map((s) => (
            <Tab key={s.key} value={s.key} disableIndicator>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                <span>{s.label}</span>
                {counters[s.key] > 0 && (
                  <Chip size="sm" variant="soft"
                    color={s.key === 'pending' ? 'primary' : s.key === 'rework' ? 'danger' : 'neutral'}
                    sx={{ '--Chip-minHeight': '18px', fontSize: '0.62rem', fontWeight: 700 }}>
                    {counters[s.key]}
                  </Chip>
                )}
              </Stack>
            </Tab>
          ))}
        </TabList>
      </Tabs>

      {/* ── Фильтры ── */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
        <Input
          size="sm" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Адрес или номер сделки"
          startDecorator={<SearchRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <Badge invisible={!filtersOn} color="primary" variant="solid" size="sm">
          <Button size="sm" variant={showFilters ? 'solid' : 'soft'} color="neutral"
            startDecorator={<TuneRoundedIcon />}
            onClick={() => setShowFilters((v) => !v)}>
            Фильтры
          </Button>
        </Badge>
        {filtersOn && (
          <Button size="sm" variant="plain" color="neutral" onClick={resetFilters}>
            Сбросить
          </Button>
        )}
      </Stack>

      <AnimatePresence initial={false}>
        {showFilters && (
          <Box component={motion.div}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            sx={{ overflow: 'hidden', mb: 1.5 }}
          >
            <Sheet variant="soft" sx={{ p: 1.5, borderRadius: '12px' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <Select size="sm" placeholder="Чертёжник" value={draftsman || null}
                  onChange={(_, v) => setDraftsman(v || '')} sx={{ minWidth: 190 }}>
                  <Option value="">Все чертёжники</Option>
                  {options.draftsmen.map((d) => (
                    <Option key={d.id} value={String(d.id)}>{d.name}</Option>
                  ))}
                </Select>
                <Select size="sm" placeholder="Город" value={city || null}
                  onChange={(_, v) => setCity(v || '')} sx={{ minWidth: 170 }}>
                  <Option value="">Все города</Option>
                  {options.cities.map((c) => (
                    <Option key={c.id} value={String(c.id)}>{c.name}</Option>
                  ))}
                </Select>
                <Input size="sm" type="date" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  startDecorator={<Typography level="body-xs">с</Typography>} />
                <Input size="sm" type="date" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  startDecorator={<Typography level="body-xs">по</Typography>} />
              </Stack>
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.75 }}>
                Даты — по моменту сдачи чертежа на проверку.
              </Typography>
            </Sheet>
          </Box>
        )}
      </AnimatePresence>

      {loadError && <Alert color="danger" sx={{ mb: 1.5 }}>
        <Typography level="body-sm">{loadError}{rows.length ? ' Показаны последние загруженные данные.' : ''}</Typography>
      </Alert>}
      {/* ── Список ── */}
      {loading && !rows.length ? (
        <Stack sx={{ alignItems: 'center', py: 6 }} spacing={1.5}>
          <CircularProgress />
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>Загружаем…</Typography>
        </Stack>
      ) : !rows.length && !loadError ? (
        <Sheet variant="soft" sx={{
          borderRadius: '16px', py: 6, textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
        }}>
          <InboxRoundedIcon sx={{ fontSize: 38, color: 'text.tertiary', opacity: 0.5 }} />
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            {filtersOn ? 'Под фильтры ничего не попало.' : (EMPTY_TEXT[scope] || 'Пусто.')}
          </Typography>
        </Sheet>
      ) : (
        <Box sx={{
          display: 'grid', gap: 1.5,
          gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
          alignItems: 'start',
        }}>
          <AnimatePresence mode="popLayout">
            {rows.map((card, i) => (
              <QualityEventCard
                key={card.id}
                card={card}
                index={i}
                reviews={reviewsById[card.id]}
                onExpand={loadReviews}
                onRefresh={load}
                onApprove={(stars) => approve(card, stars)}
                onRework={() => setReworkFor(card)}
                onChat={(chatId) => setChatModal?.({
                  open: true, chatId,
                  chatName: `Контроль качества · №${card.deal_id ?? card.id}`,
                })}
                onOpenEvent={openEvent}
              />
            ))}
          </AnimatePresence>
        </Box>
      )}

      <QualityEventModal
        open={!!detailId}
        eventId={detailId}
        token={tokenEmployee}
        onClose={() => setDetailId(null)}
        onApprove={(stars, currentCard) => approve(currentCard || rows.find((r) => r.id === detailId) || { id: detailId }, stars)}
        onRework={(currentCard) => setReworkFor(currentCard || rows.find((r) => r.id === detailId) || { id: detailId })}
        onChat={(chatId) => setChatModal?.({
          open: true, chatId, chatName: `Контроль качества · №${detailId}`,
        })}
      />

      <QualityReworkModal
        open={!!reworkFor}
        card={reworkFor}
        onClose={() => setReworkFor(null)}
        onSubmit={submitRework}
      />
    </Box>
  );
}
